import { filter, forEach, isServer, releaseUtils } from '@tunein/web-utils';
import { parse } from 'cookie';
import { merge } from 'es-toolkit';
import isEmpty from 'lodash/isEmpty';
import { GraphQLClient } from './graphql';
const defaultTimeout = 5000;
const defaultRetryLimit = 0;
const defaultPartnerId = 'qZjjnm85';
const defaultStreamFormats = 'mp3,aac,ogg,flash';
export default function createApiContext(config, requestHeaders, onTrackingEvent) {
    const tokens = {};
    const headers = {
        'Content-Type': 'application/json',
        ...(isServer() && { ALLOW_INSECURE_AUTH: true }),
        ...requestHeaders,
    };
    const reportingContext = {};
    function setAuthHeader(token) {
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    }
    function clearAuthHeader() {
        delete headers.Authorization;
    }
    function addHeaders(newHeaders) {
        Object.assign(headers, newHeaders);
    }
    function clearHeaders(headersList) {
        headersList?.forEach((header) => delete headers[header]);
    }
    function setTokens(tokenData, isInternal) {
        if (!isEmpty(tokenData)) {
            tokens.accessToken = tokenData.accessToken;
            tokens.refreshToken = tokenData.refreshToken;
            tokens.expiresIn = tokenData.expiresIn;
            if (config.onSetNewTokens && isInternal) {
                config.onSetNewTokens(this, tokens);
            }
            setAuthHeader(tokenData.accessToken);
        }
    }
    function clearTokens() {
        delete tokens.accessToken;
        delete tokens.refreshToken;
        delete tokens.expiresIn;
        clearAuthHeader();
    }
    function getTokens() {
        return tokens;
    }
    function setCookies(cookieString, shouldOverride) {
        if (headers.cookie && !shouldOverride) {
            headers.cookie = `${headers.cookie}; ${cookieString}`;
        }
        else {
            headers.cookie = cookieString;
        }
    }
    function setCookiesFromHeaders(headerCookies) {
        if (!(headerCookies && config.cookieAllowlist)) {
            return;
        }
        try {
            const parsedCookies = headerCookies.map(parse);
            const filteredCookies = filter(Object.fromEntries(parsedCookies.flatMap((set) => Object.entries(set))), (value, name) => config.cookieAllowlist?.[name]);
            forEach(filteredCookies, (value, name) => {
                setCookies(`${name}=${value}`);
                config.onSetCookiesFromHeaders?.(name, value);
            });
        }
        catch (e) {
        }
    }
    function getCookies() {
        return headers.cookie || '';
    }
    function setPartnerId(partnerId) {
        this.partnerId = partnerId;
        this.params.partnerId = partnerId;
    }
    function setAbTestIds(abTestIds) {
        this.abTestIds = abTestIds;
    }
    function setUsername(username) {
        this.params.username = username;
    }
    function setLatLong(lat, long) {
        if (Number.isFinite(Number(lat)) && Number.isFinite(Number(long))) {
            this.params.latlon = `${lat},${long}`;
        }
    }
    function setSerial(serial) {
        config.serial = serial;
        this.params.serial = serial;
        clearTokens();
    }
    function updateReportingContext(newContext) {
        merge(reportingContext, newContext);
    }
    const { streamFormats, serial, partnerId, version, desktopVersion, tid, mode, productMode, username, timeout, apiHttpsAgent, retryLimit, logCookieNames, domains, abTestIds, disableFormatsQueryParam, graphQlTransformAllowlist, } = config;
    const apiContext = {
        params: {
            formats: disableFormatsQueryParam
                ? undefined
                : streamFormats || defaultStreamFormats,
            serial,
            partnerId: partnerId || defaultPartnerId,
            version: releaseUtils.calculateReleaseVersion(version),
            desktopVersion: releaseUtils.calculateReleaseVersion(desktopVersion),
            itemUrlScheme: 'secure',
            tid,
            mode,
            productMode,
            username,
        },
        headers,
        clearTokens,
        getTokens,
        track: (event) => {
            if (onTrackingEvent) {
                onTrackingEvent(event);
            }
        },
        timeout: timeout || defaultTimeout,
        apiHttpsAgent: apiHttpsAgent || null,
        retryLimit: retryLimit || defaultRetryLimit,
        setCookies,
        getCookies,
        setCookiesFromHeaders,
        addHeaders,
        clearHeaders,
        logCookieNames,
        isGraphQlEnabled: false,
        domains,
        abTestIds,
        partnerId,
        appVersion: version,
        desktopVersion,
        reportingContext,
        graphQlSdk: new GraphQLClient(domains.graphQl, headers, graphQlTransformAllowlist).sdk,
    };
    apiContext.setTokens = setTokens.bind(apiContext);
    apiContext.setPartnerId = setPartnerId.bind(apiContext);
    apiContext.setAbTestIds = setAbTestIds.bind(apiContext);
    apiContext.setUsername = setUsername.bind(apiContext);
    apiContext.setLatLong = setLatLong.bind(apiContext);
    apiContext.setSerial = setSerial.bind(apiContext);
    apiContext.updateReportingContext = updateReportingContext.bind(apiContext);
    return apiContext;
}
